/* Nexora product template – vanilla JS, no dependencies. */
(() => {
  if (customElements.get('nexora-product')) return;

  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = format || '${{amount}}';
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    const match = fmt.match(pattern);
    if (!match) return fmt;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(pattern, value);
  };

  const locale = document.documentElement.lang || navigator.language || 'en';

  const addDays = (date, days, skipWeekends) => {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (skipWeekends && (d.getDay() === 0 || d.getDay() === 6)) continue;
      added += 1;
    }
    if (skipWeekends) {
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    }
    return d;
  };

  /* ───────────── Testimonials carousel ───────────── */
  class NexoraTestimonials extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-nx-slide]')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.querySelector('[data-nx-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-nx-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      if (this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.start();
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
      }
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.stop(); this.go(i); }
      });
    }
    disconnectedCallback() { this.stop(); }
    start() { this.stop(); this.timer = setInterval(() => this.go(this.index + 1), 5000); }
    stop() { clearInterval(this.timer); }
    go(i, user) {
      if (user) this.stop();
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => {
        const active = n === this.index;
        s.hidden = !active;
        s.classList.toggle('is-active', active);
      });
    }
  }
  if (!customElements.get('nexora-testimonials')) customElements.define('nexora-testimonials', NexoraTestimonials);

  /* ───────────── Main product ───────────── */
  class NexoraProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-nx-product-json]');
      this.data = json ? JSON.parse(json.textContent) : { variants: [] };
      this.form = this.querySelector('form[data-nx-form]');
      this.idInput = this.querySelector('[data-nx-variant-id]');
      this.qtyField = this.querySelector('[data-nx-qty]') || this.querySelector('[data-nx-qty-visible]');
      this.planInput = this.querySelector('[data-nx-selling-plan]');
      this.variant = this.data.variants.find((v) => String(v.id) === String(this.idInput?.value))
        || this.data.variants[0];

      this.initGallery();
      this.initOptions();
      this.initOffers();
      this.initQuantity();
      this.initForm();
      this.initDelivery();
      this.initCountdown();
      this.initSticky();
      this.renderVariant(false);
    }

    disconnectedCallback() {
      clearInterval(this.countdownTimer);
      this.resizeObserver?.disconnect();
      this.stickyObserver?.disconnect();
      document.body.style.removeProperty('--nx-sticky-offset');
    }

    /* ---------- Gallery ---------- */
    initGallery() {
      this.track = this.querySelector('[data-nx-track]');
      if (!this.track) return;
      this.slides = [...this.track.querySelectorAll('[data-nx-media-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-nx-thumb]')];
      this.thumbList = this.querySelector('[data-nx-thumbs]');
      this.counter = this.querySelector('[data-nx-gallery-current]');
      this.mediaIndex = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

      this.querySelector('[data-nx-gallery-prev]')?.addEventListener('click', () => this.showMedia(this.mediaIndex - 1));
      this.querySelector('[data-nx-gallery-next]')?.addEventListener('click', () => this.showMedia(this.mediaIndex + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.showMedia(Number(t.dataset.nxThumb))));
      this.querySelector('[data-nx-thumbs-prev]')?.addEventListener('click', () => this.thumbList.scrollBy({ left: -this.thumbList.clientWidth * 0.75, behavior: 'smooth' }));
      this.querySelector('[data-nx-thumbs-next]')?.addEventListener('click', () => this.thumbList.scrollBy({ left: this.thumbList.clientWidth * 0.75, behavior: 'smooth' }));

      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking || this.programmaticScroll) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.mediaIndex && this.slides[i]) this.setActiveMedia(i);
        });
      }, { passive: true });

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.showMedia(this.mediaIndex - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.showMedia(this.mediaIndex + 1); }
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.fitHeight());
        this.slides.forEach((s) => this.resizeObserver.observe(s));
      }
      this.showMedia(this.mediaIndex, false);
      this.initLightbox();
    }

    showMedia(i, smooth = true) {
      if (!this.slides?.length) return;
      const index = (i + this.slides.length) % this.slides.length;
      this.programmaticScroll = true;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => { this.programmaticScroll = false; }, smooth ? 700 : 50);
      this.track.scrollTo({ left: this.track.clientWidth * index, behavior: smooth ? 'smooth' : 'auto' });
      this.setActiveMedia(index);
    }

    setActiveMedia(index) {
      this.mediaIndex = index;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
      this.slides.forEach((s, n) => {
        if (n !== index) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t, n) => {
        const active = n === index;
        t.classList.toggle('is-active', active);
        if (active) {
          t.setAttribute('aria-current', 'true');
          // Instant scroll: a second smooth scroll would cancel the main track's smooth scroll in Chromium.
          const list = this.thumbList;
          const tRect = t.getBoundingClientRect();
          const listRect = list.getBoundingClientRect();
          list.scrollLeft += tRect.left - listRect.left - (list.clientWidth - tRect.width) / 2;
        } else {
          t.removeAttribute('aria-current');
        }
      });
      if (this.counter) this.counter.textContent = index + 1;
      this.fitHeight();
    }

    fitHeight() {
      const slide = this.slides?.[this.mediaIndex];
      if (!slide) return;
      const frame = slide.querySelector('.nx-gallery__frame');
      if (frame && frame.offsetHeight) this.track.style.height = `${frame.offsetHeight}px`;
    }

    initLightbox() {
      this.lightbox = this.querySelector('[data-nx-lightbox]');
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this.lbImages = [...this.lightbox.querySelectorAll('[data-nx-lightbox-img]')];
      this.querySelectorAll('[data-nx-zoom]').forEach((btn) => {
        btn.addEventListener('click', () => this.openLightbox(Number(btn.dataset.nxZoom)));
      });
      this.lightbox.querySelector('[data-nx-lightbox-close]').addEventListener('click', () => this.lightbox.close());
      this.lightbox.querySelector('[data-nx-lightbox-prev]').addEventListener('click', () => this.stepLightbox(-1));
      this.lightbox.querySelector('[data-nx-lightbox-next]').addEventListener('click', () => this.stepLightbox(1));
      this.lightbox.addEventListener('click', (e) => { if (e.target === this.lightbox) this.lightbox.close(); });
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        if (e.key === 'ArrowRight') this.stepLightbox(1);
      });
    }

    openLightbox(mediaIndex) {
      const pos = this.lbImages.findIndex((img) => Number(img.dataset.nxLightboxImg) === mediaIndex);
      this.lbPos = pos > -1 ? pos : 0;
      this.renderLightbox();
      this.lightbox.showModal();
      document.documentElement.classList.add('nx-lock');
      this.lightbox.addEventListener('close', () => document.documentElement.classList.remove('nx-lock'), { once: true });
    }

    stepLightbox(dir) {
      this.lbPos = (this.lbPos + dir + this.lbImages.length) % this.lbImages.length;
      this.renderLightbox();
    }

    renderLightbox() {
      this.lbImages.forEach((img, n) => {
        const active = n === this.lbPos;
        if (active && !img.src) img.src = img.dataset.src;
        img.hidden = !active;
      });
    }

    /* ---------- Variant options ---------- */
    initOptions() {
      this.optionInputs = [...this.querySelectorAll('[data-nx-option]')];
      this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      this.updateOptionAvailability();
    }

    selectedOptions() {
      const values = [];
      this.optionInputs.forEach((input) => {
        const i = Number(input.dataset.nxOption);
        if (input.tagName === 'SELECT' || input.checked) values[i] = input.value;
      });
      return values;
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      this.variant = this.data.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
      selected.forEach((value, i) => {
        const label = this.querySelector(`[data-nx-option-label="${i}"]`);
        if (label) label.textContent = value;
      });
      this.updateOptionAvailability();
      this.renderVariant(true);
    }

    updateOptionAvailability() {
      const selected = this.selectedOptions();
      this.optionInputs.forEach((input) => {
        const i = Number(input.dataset.nxOption);
        const test = (value) => this.data.variants.some((v) => v.available
          && v.options[i] === value
          && v.options.every((o, n) => n === i || n > i || o === selected[n]));
        if (input.tagName === 'SELECT') {
          [...input.options].forEach((opt) => { opt.dataset.unavailable = String(!test(opt.value)); });
        } else {
          input.classList.toggle('is-unavailable', !test(input.value));
        }
      });
    }

    /* ---------- Offers & subscription ---------- */
    initOffers() {
      this.offers = this.querySelector('[data-nx-offers]');
      if (!this.offers) return;
      this.tiers = [...this.offers.querySelectorAll('[data-nx-tier]')];
      this.picks = this.offers.querySelector('[data-nx-picks]');
      this.offersError = this.offers.querySelector('[data-nx-offers-error]');
      this.tiers.forEach((tier) => {
        tier.querySelector('input').addEventListener('change', () => {
          this.userSelected = true;
          this.selectTier(tier);
        });
        // Clicking the already-selected tier still counts as an explicit choice.
        tier.addEventListener('click', () => {
          this.userSelected = true;
          this.updateSticky();
        });
      });
      const checked = this.tiers.find((t) => t.querySelector('input').checked) || this.tiers[0];
      if (checked) {
        checked.querySelector('input').checked = true;
        this.selectTier(checked);
      }

      this.purchaseInputs = [...this.offers.querySelectorAll('[data-nx-purchase]')];
      this.planSelect = this.offers.querySelector('[data-nx-plan-select]');
      this.planWrap = this.offers.querySelector('[data-nx-plan-wrap]');
      this.purchaseInputs.forEach((input) => input.addEventListener('change', () => this.onPurchaseChange()));
      this.planSelect?.addEventListener('change', () => this.onPurchaseChange());
      if (this.purchaseInputs.length) this.onPurchaseChange();
    }

    selectTier(tier) {
      this.activeTier = tier;
      this.tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
      if (this.qtyField) this.qtyField.value = tier.dataset.qty;
      this.renderPicks();
      this.hideMessages();
      this.updateSticky();
    }

    /* One required variant select per unit, only for products with several variants. */
    renderPicks() {
      if (!this.picks) return;
      const list = this.picks.querySelector('[data-nx-picks-list]');
      const qty = Number(this.activeTier?.dataset.qty) || 1;
      const previous = [...list.querySelectorAll('select')].map((s) => s.value);
      const label = this.picks.dataset.label || 'Item';
      list.textContent = '';
      for (let i = 0; i < qty; i += 1) {
        const row = document.createElement('label');
        row.className = 'nx-picks__row';
        const name = document.createElement('span');
        name.className = 'nx-picks__label';
        name.textContent = `${label} ${i + 1}`;
        const select = document.createElement('select');
        select.dataset.nxPick = '';
        select.required = true;
        select.add(new Option('Select…', ''));
        this.data.variants.forEach((v) => {
          const opt = new Option(v.available ? v.title : `${v.title} – sold out`, String(v.id));
          opt.disabled = !v.available;
          select.add(opt);
        });
        select.value = previous[i] || '';
        select.addEventListener('change', () => {
          this.userSelected = true;
          select.classList.remove('is-invalid');
          this.hideMessages();
          this.updateSticky();
        });
        const box = document.createElement('span');
        box.className = 'nx-select';
        box.append(select);
        row.append(name, box);
        list.append(row);
      }
    }

    subscribing() {
      return this.purchaseInputs?.some((i) => i.checked && i.value === 'subscribe');
    }

    onPurchaseChange() {
      const sub = this.subscribing();
      if (this.planInput) {
        this.planInput.disabled = !sub;
        if (this.planSelect) this.planInput.value = this.planSelect.value;
      }
      if (this.planWrap && this.planSelect) {
        this.planWrap.hidden = !(sub && this.planSelect.options.length > 1);
      }
      this.purchaseInputs.forEach((i) => i.closest('.nx-plan')?.classList.toggle('is-selected', i.checked));
      this.renderPrices();
    }

    unitPrice() {
      const v = this.variant;
      if (!v) return 0;
      if (this.subscribing() && this.planInput) {
        const planPrice = v.selling_plans?.[this.planInput.value];
        if (planPrice != null) return planPrice;
      }
      return v.price;
    }

    /* ---------- Quantity ---------- */
    initQuantity() {
      const input = this.querySelector('[data-nx-qty-visible]');
      if (!input) return;
      const step = () => Number(input.step) || 1;
      const clamp = (n) => {
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        return Math.min(Math.max(n, min), max);
      };
      this.querySelector('[data-nx-qty-minus]')?.addEventListener('click', () => { input.value = clamp(Number(input.value) - step()); });
      this.querySelector('[data-nx-qty-plus]')?.addEventListener('click', () => { input.value = clamp(Number(input.value) + step()); });
      input.addEventListener('change', () => { input.value = clamp(Math.round(Number(input.value) || 1)); });
    }

    /* ---------- Rendering ---------- */
    money(cents, withCurrency) {
      return formatMoney(cents, withCurrency ? this.data.moneyWithCurrencyFormat : this.data.moneyFormat);
    }

    renderVariant(changed) {
      const v = this.variant;
      if (this.idInput) {
        this.idInput.value = v ? v.id : '';
        this.idInput.disabled = !v;
      }
      this.renderButton();
      this.renderPrices();
      this.renderStock();

      if (!changed || !v) return;
      if (v.featured_media_id && this.slides) {
        const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(v.featured_media_id));
        if (i > -1) this.showMedia(i);
      }
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.hideMessages();
      this.dispatchEvent(new CustomEvent('nexora:variant-change', { bubbles: true, detail: { variant: v } }));
    }

    renderButton() {
      const btn = this.querySelector('[data-nx-atc]');
      if (!btn) return;
      const text = btn.querySelector('[data-nx-atc-text]');
      const v = this.variant;
      let label = btn.dataset.labelAdd;
      if (!v) label = btn.dataset.labelUnavailable;
      else if (!v.available) label = btn.dataset.labelSoldout;
      btn.disabled = !v || !v.available;
      text.textContent = label;
    }

    renderPrices() {
      const v = this.variant;
      const unit = this.unitPrice();

      const priceBlock = this.querySelector('[data-nx-price-block]');
      if (priceBlock && v) {
        const withCurrency = priceBlock.dataset.currency === 'true';
        const onSale = v.compare_at_price > v.price;
        priceBlock.querySelector('[data-nx-price]').textContent = this.money(unit, withCurrency);
        const compare = priceBlock.querySelector('[data-nx-compare]');
        const compareCents = onSale ? v.compare_at_price : (unit < v.price ? v.price : 0);
        compare.hidden = !compareCents;
        compare.textContent = compareCents ? this.money(compareCents) : '';
        const badge = priceBlock.querySelector('[data-nx-save]');
        if (badge) {
          const pct = compareCents ? Math.round(((compareCents - unit) * 100) / compareCents) : 0;
          badge.hidden = pct <= 0;
          badge.textContent = badge.dataset.template.replace('[percent]', pct);
        }
        const unitEl = priceBlock.querySelector('[data-nx-unit-price]');
        if (unitEl) {
          unitEl.hidden = !v.unit_price || !v.unit_measure;
          if (v.unit_price && v.unit_measure) {
            const [ref, refUnit] = v.unit_measure.split('|');
            unitEl.textContent = `${this.money(v.unit_price)}/${ref !== '1' ? ref : ''}${refUnit}`;
          }
        }
      }

      if (this.tiers && v) {
        const compareUnit = Math.max(v.compare_at_price || 0, v.price);
        this.tiers.forEach((tier) => {
          const qty = Number(tier.dataset.qty) || 1;
          const free = Number(tier.dataset.free) || 0;
          const paid = free < qty ? qty - free : qty;
          // Matches the automatic Buy X Get Y discount: free units are charged at $0 in cart/checkout.
          const price = unit * paid;
          const compare = compareUnit * qty;
          const pct = compare > price ? Math.round(((compare - price) * 100) / compare) : 0;
          tier.querySelector('[data-nx-tier-price]').textContent = this.money(price);
          const compareEl = tier.querySelector('[data-nx-tier-compare]');
          compareEl.hidden = compare <= price;
          compareEl.textContent = this.money(compare);
          const label = tier.querySelector('[data-nx-tier-label]');
          if (label) {
            label.hidden = pct <= 0;
            label.textContent = label.dataset.template.replace('[percent]', `${pct}%`);
          }
        });
      }
      this.updateSticky();
    }

    renderStock() {
      const el = this.querySelector('[data-nx-stock]');
      if (!el) return;
      const v = this.variant;
      let state = 'in';
      let text = el.dataset.in;
      if (!v || !v.available) {
        state = 'out';
        text = el.dataset.out;
      } else if (v.inventory_management === 'shopify' && v.inventory_policy === 'deny'
        && v.inventory_quantity <= Number(el.dataset.threshold)) {
        state = 'low';
        text = el.dataset.low.replace('[count]', v.inventory_quantity);
      }
      el.classList.remove('nx-stock--in', 'nx-stock--low', 'nx-stock--out');
      el.classList.add(`nx-stock--${state}`);
      el.querySelector('[data-nx-stock-text]').textContent = text;
    }

    /* ---------- Add to cart (real Shopify cart) ---------- */
    initForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    hideMessages() {
      const err = this.querySelector('[data-nx-error]');
      const ok = this.querySelector('[data-nx-success]');
      if (err) err.hidden = true;
      if (ok) ok.hidden = true;
      if (this.offersError) this.offersError.hidden = true;
    }

    showError(message, nearSelector) {
      const err = this.querySelector('[data-nx-error]');
      if (err) {
        err.textContent = message;
        err.hidden = false;
      }
      if (nearSelector && this.offersError) {
        this.offersError.textContent = message;
        this.offersError.hidden = false;
      }
    }

    /**
     * Builds the exact cart lines for the current selection.
     * Same variant chosen several times => one line with quantity > 1.
     * Different variants => separate lines, never merged.
     */
    getLineItems() {
      if (this.picks) {
        const selects = [...this.picks.querySelectorAll('[data-nx-pick]')];
        const missing = selects.filter((s) => !s.value);
        if (missing.length) {
          missing.forEach((s) => s.classList.add('is-invalid'));
          return { error: 'Please choose an option for every item in your bundle.', focus: missing[0] };
        }
        const counts = new Map();
        selects.forEach((s) => counts.set(s.value, (counts.get(s.value) || 0) + 1));
        return { items: [...counts].map(([id, quantity]) => ({ id: Number(id), quantity })) };
      }
      const v = this.variant;
      if (!v) return { error: 'Please select an available option.' };
      if (!v.available) return { error: 'This option is sold out.' };
      const quantity = Math.max(1, Math.round(Number(this.qtyField?.value) || 1));
      const item = { id: v.id, quantity };
      if (this.planInput && !this.planInput.disabled && this.planInput.value) {
        item.selling_plan = Number(this.planInput.value);
      }
      return { items: [item] };
    }

    async addToCart() {
      const btn = this.querySelector('[data-nx-atc]');
      if (this.busy) return false;
      this.hideMessages();

      const { items, error, focus } = this.getLineItems();
      if (error) {
        this.showError(error, true);
        focus?.focus();
        return false;
      }

      this.busy = true;
      btn?.setAttribute('aria-busy', 'true');
      btn?.classList.add('is-loading');
      this.stickyBtn?.classList.add('is-loading');
      const done = () => {
        this.busy = false;
        btn?.removeAttribute('aria-busy');
        btn?.classList.remove('is-loading');
        this.stickyBtn?.classList.remove('is-loading');
      };

      const mode = this.dataset.afterAdd;
      const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      // Use the theme's own cart event so its cart drawer and cart count update exactly as they do
      // for the theme's product form. Falls back to the cart page when unavailable.
      let events = null;
      let deferred = null;
      if (mode === 'ajax') {
        try {
          events = await import('@shopify/events');
        } catch (e) {
          events = null;
        }
        if (events?.CartLinesUpdateEvent) {
          deferred = events.CartLinesUpdateEvent.createPromise();
          this.dispatchEvent(new events.CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
            promise: deferred.promise,
          }));
        }
      }

      let res;
      let data;
      try {
        res = await fetch(`${this.dataset.cartAddUrl || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        });
        data = await res.json();
      } catch (err) {
        deferred?.reject(err);
        this.showError('Network error. Please try again.');
        done();
        return false;
      }

      const cart = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => null);

      if (!res.ok || data.status) {
        this.showError(data.description || data.message || 'Could not add to cart. Please try again.');
        if (deferred && cart) {
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: true, items: cart.items, source: 'product-form-component',
              sourceId: this.id, itemCount: totalQuantity, productId: this.dataset.productId,
            },
          });
        } else {
          deferred?.reject(new Error('add failed'));
        }
        done();
        return false;
      }

      if (mode === 'checkout') {
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        return true;
      }
      if (mode === 'cart' || !deferred || !cart) {
        deferred?.reject(new Error('redirecting'));
        window.location.href = this.dataset.cartUrl;
        return true;
      }

      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items, source: 'product-form-component', sourceId: this.id,
          itemCount: totalQuantity, productId: this.dataset.productId,
          sections: data.sections, didError: false,
        },
      });
      const ok = this.querySelector('[data-nx-success]');
      if (ok) ok.hidden = false;
      done();
      return true;
    }

    /* ---------- Sticky add to cart ---------- */
    initSticky() {
      this.sticky = this.querySelector('[data-nx-sticky]');
      if (!this.sticky) return;
      this.stickyBtn = this.sticky.querySelector('[data-nx-sticky-btn]');
      this.stickyPrice = this.sticky.querySelector('[data-nx-sticky-price]');
      this.buyArea = this.querySelector('[data-nx-buy-area]') || this.querySelector('[data-nx-purchase-area]');
      this.purchaseArea = this.querySelector('[data-nx-purchase-area]') || this.buyArea;
      if (!this.buyArea || !('IntersectionObserver' in window)) return;

      // With no bundle or variant choice to make there is nothing to confirm first.
      if (this.dataset.hasOffers !== 'true' && this.dataset.multiVariant !== 'true') this.userSelected = true;

      this.stickyObserver = new IntersectionObserver(([entry]) => {
        // Show only once the buy area has been scrolled past (it is above the viewport).
        this.setStickyVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.stickyObserver.observe(this.buyArea);
      this.stickyBtn?.addEventListener('click', () => this.onStickyClick());
      this.updateSticky();
    }

    setStickyVisible(show) {
      if (!this.sticky || this.stickyShown === show) return;
      this.stickyShown = show;
      if (show) {
        this.sticky.hidden = false;
        requestAnimationFrame(() => {
          this.sticky.classList.add('is-visible');
          document.body.style.setProperty('--nx-sticky-offset', `${this.sticky.offsetHeight}px`);
          document.body.classList.add('nx-has-sticky');
        });
      } else {
        this.sticky.classList.remove('is-visible');
        document.body.classList.remove('nx-has-sticky');
        setTimeout(() => { if (!this.stickyShown) this.sticky.hidden = true; }, 250);
      }
    }

    updateSticky() {
      if (!this.sticky || !this.stickyBtn) return;
      const v = this.variant;
      const tierPrice = this.activeTier?.querySelector('[data-nx-tier-price]')?.textContent;
      if (this.stickyPrice) {
        this.stickyPrice.textContent = tierPrice || (v ? this.money(this.unitPrice()) : '');
      }
      const available = this.picks ? this.data.variants.some((x) => x.available) : v && v.available;
      this.stickyBtn.disabled = !available;
      if (!available) {
        this.stickyBtn.textContent = 'Sold out';
      } else if (this.userSelected) {
        this.stickyBtn.textContent = this.sticky.dataset.labelAdd;
      } else {
        this.stickyBtn.textContent = this.sticky.dataset.labelChoose;
      }
    }

    onStickyClick() {
      if (!this.userSelected) {
        this.scrollToPurchase();
        return;
      }
      const { error } = this.getLineItems();
      if (error) {
        this.scrollToPurchase();
        this.showError(error, true);
        return;
      }
      this.addToCart();
    }

    scrollToPurchase() {
      const target = this.purchaseArea;
      if (!target) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      target.classList.remove('nx-highlight');
      void target.offsetWidth; // restart the highlight animation
      target.classList.add('nx-highlight');
      const focusable = target.querySelector('.nx-tier.is-selected input, [data-nx-pick]:invalid, input, select');
      setTimeout(() => focusable?.focus({ preventScroll: true }), reduce ? 0 : 450);
    }

    /* ---------- Delivery estimate & cutoff timer ---------- */
    initDelivery() {
      const el = this.querySelector('[data-nx-delivery]');
      if (!el) return;
      const skip = el.dataset.skipWeekends === 'true';
      const fmt = { month: 'short', day: 'numeric' };
      const from = addDays(new Date(), Number(el.dataset.min) || 0, skip).toLocaleDateString(locale, fmt);
      const to = addDays(new Date(), Number(el.dataset.max) || 0, skip).toLocaleDateString(locale, fmt);
      const dates = from === to ? from : `${from} – ${to}`;
      const target = el.querySelector('[data-nx-delivery-text]');
      const parts = el.dataset.template.split('[dates]');
      target.textContent = '';
      parts.forEach((part, i) => {
        target.append(part);
        if (i < parts.length - 1) {
          const strong = document.createElement('strong');
          strong.textContent = dates;
          target.append(strong);
        }
      });
    }

    initCountdown() {
      const el = this.querySelector('[data-nx-countdown]');
      if (!el) return;
      const cutoff = Number(el.dataset.cutoff) || 0;
      const days = Number(el.dataset.days) || 0;
      const skip = el.dataset.skipWeekends === 'true';
      const heading = el.querySelector('[data-nx-countdown-heading]');
      const text = el.querySelector('[data-nx-countdown-text]');

      const tick = () => {
        const now = new Date();
        const target = new Date(now);
        target.setHours(cutoff, 0, 0, 0);
        const shipDay = new Date(now);
        if (now >= target) {
          target.setDate(target.getDate() + 1);
          shipDay.setDate(shipDay.getDate() + 1);
        }
        const diff = Math.max(0, target - now);
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const date = addDays(shipDay, days, skip).toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' });
        heading.textContent = el.dataset.heading.replace('[date]', date);
        text.innerHTML = el.dataset.template.replace('[hours]', hours).replace('[minutes]', minutes);
      };
      tick();
      this.countdownTimer = setInterval(tick, 30000);
    }
  }

  customElements.define('nexora-product', NexoraProduct);
})();
